import { MoreVertical, Phone, Route as RouteIcon, Users } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import type { TransportVehicle } from '@/types/transport';
import { VEHICLE_STATUS } from '../constants';

export interface VehicleCardProps {
  vehicle: TransportVehicle;
  onMenu?: (vehicle: TransportVehicle) => void;
}

/**
 * One vehicle as a card: its standing, who is aboard it, and what it runs.
 *
 * The mock this was drawn from puts a photograph of each bus here. A stock
 * photograph is a picture of somebody else's vehicle, and a real one is an
 * upload this section has no endpoint for yet — so the tile is a drawn glyph,
 * which is honest and, unlike a photo, tells a bus from a van at 40 pixels.
 *
 * Both people are shown, not just the driver. A second adult rides with the
 * children at most Indian schools, and an office ringing about a child on the
 * bus needs whoever is actually beside them.
 */
export function VehicleCard({ vehicle, onMenu }: VehicleCardProps) {
  const status = VEHICLE_STATUS[vehicle.status];

  return (
    <article className="flex flex-col gap-3 rounded-3xl border border-line bg-surface p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <header className="flex items-start justify-between gap-2">
        <Badge tone={status.tone} withDot>
          {status.label}
        </Badge>
        <button
          type="button"
          onClick={() => onMenu?.(vehicle)}
          title={`More for ${vehicle.registrationNumber}`}
          aria-label={`More for ${vehicle.registrationNumber}`}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-xl text-ink-3 transition-colors hover:bg-surface-2 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </header>

      <div className="flex items-center gap-3">
        <VehicleGlyph kind={vehicle.kind} status={vehicle.status} />
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-ink">{vehicle.registrationNumber}</p>
          <p className="truncate text-xs font-semibold text-ink-3">
            {vehicle.kind === 'BUS' ? 'Bus' : 'Van'}
          </p>
        </div>
      </div>

      <dl className="flex flex-col gap-2.5 border-t border-line pt-3">
        <Person label="Driver" name={vehicle.driverName} mobile={vehicle.driverMobile} />
        <Person label="Supporter" name={vehicle.supporterName} mobile={vehicle.supporterMobile} />

        <div className="min-w-0">
          <dt className="lbl text-ink-3">Route</dt>
          <dd className="mt-0.5 flex min-w-0 items-center gap-1.5">
            {vehicle.routeName ? (
              <>
                <RouteIcon className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
                <span className="truncate text-sm font-bold text-ink">
                  {vehicle.routeCode ? `${vehicle.routeCode} — ` : ''}
                  {vehicle.routeName}
                </span>
              </>
            ) : (
              <span className="text-sm font-bold text-ink-3">—</span>
            )}
          </dd>
        </div>
      </dl>

      <footer className="flex items-center justify-between gap-2 border-t border-line pt-3 text-xs font-bold text-ink-2">
        <span className="inline-flex items-center gap-1.5">
          <SeatIcon />
          {vehicle.capacity} seats
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5 text-ink-3" aria-hidden />
          {vehicle.studentsAssigned} students
        </span>
      </footer>
    </article>
  );
}

/** One named person on the vehicle, with the number to reach them on. */
function Person({
  label,
  name,
  mobile,
}: {
  label: string;
  name: string | null;
  mobile: string | null;
}) {
  return (
    <div className="min-w-0">
      <dt className="lbl text-ink-3">{label}</dt>
      <dd className="mt-0.5 flex min-w-0 items-center justify-between gap-2">
        <span className="truncate text-sm font-bold text-ink">{name ?? 'Not assigned'}</span>
        {mobile && (
          <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-ink-2">
            <Phone className="h-3 w-3 text-ink-3" aria-hidden />
            {mobile}
          </span>
        )}
      </dd>
    </div>
  );
}

/**
 * A bus or a van, drawn.
 *
 * Tinted by standing rather than painted one colour, so a card scanned from
 * across a desk says "in the workshop" before any word is read. The badge above
 * still says it in words — colour is never the only thing carrying a status.
 */
function VehicleGlyph({
  kind,
  status,
}: {
  kind: TransportVehicle['kind'];
  status: TransportVehicle['status'];
}) {
  const paint =
    status === 'ON_ROUTE'
      ? 'bg-ok-bg text-ok'
      : status === 'MAINTENANCE'
        ? 'bg-warn-bg text-warn'
        : 'bg-crit-bg text-crit';

  return (
    <span
      className={cn('grid h-12 w-16 shrink-0 place-items-center rounded-2xl', paint)}
      aria-hidden
    >
      <svg viewBox="0 0 40 24" className="h-6 w-10" fill="none">
        {/* The body: a bus is square-nosed and long, a van slopes at the front. */}
        <path
          d={
            kind === 'BUS'
              ? 'M3 4h30a3 3 0 0 1 3 3v9a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z'
              : 'M3 7h20l7 3h4a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z'
          }
          className="fill-current opacity-90"
        />
        {/* Windows, and the two wheels under it. */}
        <rect x="5" y="7" width="7" height="5" rx="1" className="fill-surface" />
        <rect x="14" y="7" width="7" height="5" rx="1" className="fill-surface" />
        {kind === 'BUS' && <rect x="23" y="7" width="7" height="5" rx="1" className="fill-surface" />}
        <circle cx="10" cy="19" r="3" className="fill-current" />
        <circle cx="29" cy="19" r="3" className="fill-current" />
        <circle cx="10" cy="19" r="1.2" className="fill-surface" />
        <circle cx="29" cy="19" r="1.2" className="fill-surface" />
      </svg>
    </span>
  );
}

/** A seat, which lucide has no icon for. */
function SeatIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 text-ink-3" fill="none" aria-hidden>
      <path
        d="M4 2h6a2 2 0 0 1 2 2v5H4V2Zm-1 8h10a2 2 0 0 1 2 2v2h-2v-1H3v1H1v-2a2 2 0 0 1 2-2Z"
        className="fill-current"
      />
    </svg>
  );
}
